import { useState, useEffect, useCallback } from 'react';
import {
  Stack, Group, Button, Text, Paper, SimpleGrid, Badge, Table,
  Progress, Alert, Loader, Center, Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconClock, IconPlayerPlay, IconPlayerStop, IconCalendar,
  IconBeach, IconAlertCircle,
} from '@tabler/icons-react';
import api from '@/config/api';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';

interface ClockStatus {
  clocked_in: boolean;
  current_entry: { id: number; clock_in: string; elapsed_minutes: number } | null;
}

interface TimeEntry {
  id: number;
  date: string;
  clock_in: string;
  clock_out: string | null;
  regular_hours: number;
  ot_hours: number;
  break_minutes: number;
  status: string;
}

interface HoursResponse {
  entries: TimeEntry[];
  total_hours: number;
  total_ot: number;
}

interface PTOBalance {
  leave_type: string;
  available: number;
  used: number;
  total: number;
  pending: number;
}

interface ScheduleEntry {
  id: number;
  date: string;
  shift_start: string;
  shift_end: string;
  role: string;
  department: string;
}

interface AttendanceScore {
  total_points: number;
  max_points: number;
  occurrences: {
    id: number;
    date: string;
    reason: string;
    points: number;
    expires_at: string;
  }[];
}

function getWeekRange() {
  const now = dayjs();
  const startOfWeek = now.startOf('week');
  const endOfWeek = now.endOf('week');
  return {
    start_date: startOfWeek.format('YYYY-MM-DD'),
    end_date: endOfWeek.format('YYYY-MM-DD'),
  };
}

function formatElapsed(totalSeconds: number): string {
  const hrs = Math.floor(totalSeconds / 3600);
  const mins = Math.floor((totalSeconds % 3600) / 60);
  const secs = totalSeconds % 60;
  return `${String(hrs).padStart(2, '0')}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
}

export default function MyTimeDashboard() {
  const queryClient = useQueryClient();
  const [elapsed, setElapsed] = useState(0);
  const [gpsLoading, setGpsLoading] = useState(false);

  const weekRange = getWeekRange();

  // ---------- Queries ----------

  const statusQuery = useQuery<ClockStatus>({
    queryKey: ['my-clock-status'],
    queryFn: async () => {
      const res = await api.get('/time-attendance/my/status');
      return res.data;
    },
    refetchInterval: 30_000,
  });

  const hoursQuery = useQuery<HoursResponse>({
    queryKey: ['my-hours', weekRange.start_date, weekRange.end_date],
    queryFn: async () => {
      const res = await api.get('/time-attendance/my/hours', {
        params: { start_date: weekRange.start_date, end_date: weekRange.end_date },
      });
      return res.data;
    },
  });

  const ptoQuery = useQuery<{ balances: PTOBalance[] }>({
    queryKey: ['my-pto-balance'],
    queryFn: async () => {
      const res = await api.get('/time-attendance/my/pto-balance');
      return res.data;
    },
  });

  const scheduleQuery = useQuery<{ schedules: ScheduleEntry[] }>({
    queryKey: ['my-schedule-upcoming'],
    queryFn: async () => {
      const start = dayjs().format('YYYY-MM-DD');
      const end = dayjs().add(7, 'day').format('YYYY-MM-DD');
      const res = await api.get('/time-attendance/my/schedule', {
        params: { start_date: start, end_date: end },
      });
      return res.data;
    },
  });

  const scoreQuery = useQuery<AttendanceScore>({
    queryKey: ['my-attendance-score'],
    queryFn: async () => {
      const res = await api.get('/time-attendance/my/attendance-score');
      return res.data;
    },
  });

  // ---------- Elapsed timer ----------

  useEffect(() => {
    if (!statusQuery.data?.clocked_in || !statusQuery.data.current_entry) {
      setElapsed(0);
      return;
    }
    const clockInTime = dayjs(statusQuery.data.current_entry.clock_in);
    const calcElapsed = () => Math.max(0, dayjs().diff(clockInTime, 'second'));
    setElapsed(calcElapsed());
    const interval = setInterval(() => setElapsed(calcElapsed()), 1000);
    return () => clearInterval(interval);
  }, [statusQuery.data]);

  // ---------- Mutations ----------

  const getGps = useCallback((): Promise<{ lat?: number; lon?: number }> => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve({});
        return;
      }
      setGpsLoading(true);
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setGpsLoading(false);
          resolve({ lat: pos.coords.latitude, lon: pos.coords.longitude });
        },
        () => {
          setGpsLoading(false);
          resolve({});
        },
        { timeout: 5000 },
      );
    });
  }, []);

  const clockInMutation = useMutation({
    mutationFn: async () => {
      const gps = await getGps();
      const res = await api.post('/time-attendance/my/clock-in', {
        gps_lat: gps.lat,
        gps_lon: gps.lon,
      });
      return res.data;
    },
    onSuccess: () => {
      notifications.show({ title: 'Clocked In', message: 'You are now clocked in.', color: 'green' });
      queryClient.invalidateQueries({ queryKey: ['my-clock-status'] });
      queryClient.invalidateQueries({ queryKey: ['my-hours'] });
    },
    onError: () => {
      notifications.show({ title: 'Error', message: 'Failed to clock in.', color: 'red' });
    },
  });

  const clockOutMutation = useMutation({
    mutationFn: async () => {
      const res = await api.post('/time-attendance/my/clock-out');
      return res.data;
    },
    onSuccess: (data) => {
      notifications.show({
        title: 'Clocked Out',
        message: `Regular: ${data.regular_hours?.toFixed(1) ?? 0}h, OT: ${data.ot_hours?.toFixed(1) ?? 0}h`,
        color: 'blue',
      });
      queryClient.invalidateQueries({ queryKey: ['my-clock-status'] });
      queryClient.invalidateQueries({ queryKey: ['my-hours'] });
    },
    onError: () => {
      notifications.show({ title: 'Error', message: 'Failed to clock out.', color: 'red' });
    },
  });

  // ---------- Helpers ----------

  const isClockedIn = statusQuery.data?.clocked_in ?? false;
  const isClockLoading = clockInMutation.isPending || clockOutMutation.isPending || gpsLoading;

  function getScoreColor(total: number, max: number): string {
    if (max === 0) return 'green';
    const pct = total / max;
    if (pct < 0.5) return 'green';
    if (pct < 0.75) return 'yellow';
    return 'red';
  }

  // Build week day labels
  const weekDays = Array.from({ length: 7 }, (_, i) =>
    dayjs(weekRange.start_date).add(i, 'day'),
  );

  // ---------- Render ----------

  if (statusQuery.isLoading) {
    return (
      <Center h={400}>
        <Loader size="lg" />
      </Center>
    );
  }

  return (
    <Stack gap="lg">
      <Title order={2}>My Time Dashboard</Title>

      {/* ===== Clock Widget ===== */}
      <Paper p="xl" shadow="sm" radius="md" withBorder>
        <Group justify="space-between" align="center">
          <Group gap="md">
            <IconClock size={32} />
            <div>
              <Text size="lg" fw={600}>
                {isClockedIn ? 'Currently Clocked In' : 'Not Clocked In'}
              </Text>
              {isClockedIn && (
                <Text size="xl" fw={700} ff="monospace">
                  {formatElapsed(elapsed)}
                </Text>
              )}
            </div>
            <Badge color={isClockedIn ? 'green' : 'gray'} size="lg" variant="filled">
              {isClockedIn ? 'CLOCKED IN' : 'CLOCKED OUT'}
            </Badge>
          </Group>
          <Button
            size="lg"
            color={isClockedIn ? 'red' : 'green'}
            leftSection={isClockedIn ? <IconPlayerStop size={20} /> : <IconPlayerPlay size={20} />}
            loading={isClockLoading}
            onClick={() => (isClockedIn ? clockOutMutation.mutate() : clockInMutation.mutate())}
          >
            {isClockedIn ? 'Clock Out' : 'Clock In'}
          </Button>
        </Group>
      </Paper>

      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
        {/* ===== This Week's Hours ===== */}
        <Paper p="md" shadow="sm" radius="md" withBorder>
          <Group gap="xs" mb="md">
            <IconClock size={20} />
            <Text fw={600} size="lg">This Week's Hours</Text>
          </Group>
          {hoursQuery.isLoading ? (
            <Center h={120}><Loader /></Center>
          ) : hoursQuery.isError ? (
            <Alert color="red" icon={<IconAlertCircle size={16} />}>
              Failed to load hours.
            </Alert>
          ) : (
            <>
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Day</Table.Th>
                    <Table.Th>Date</Table.Th>
                    <Table.Th style={{ textAlign: 'right' }}>Regular</Table.Th>
                    <Table.Th style={{ textAlign: 'right' }}>OT</Table.Th>
                    <Table.Th>Status</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {weekDays.map((day) => {
                    const entry = hoursQuery.data?.entries.find(
                      (e) => dayjs(e.date).format('YYYY-MM-DD') === day.format('YYYY-MM-DD'),
                    );
                    return (
                      <Table.Tr key={day.format('YYYY-MM-DD')}>
                        <Table.Td>{day.format('ddd')}</Table.Td>
                        <Table.Td>{day.format('MM/DD')}</Table.Td>
                        <Table.Td style={{ textAlign: 'right' }}>
                          {entry ? entry.regular_hours.toFixed(1) : '-'}
                        </Table.Td>
                        <Table.Td style={{ textAlign: 'right' }}>
                          {entry ? entry.ot_hours.toFixed(1) : '-'}
                        </Table.Td>
                        <Table.Td>
                          {entry ? (
                            <Badge
                              color={entry.status === 'approved' ? 'green' : entry.status === 'pending' ? 'yellow' : 'gray'}
                              size="sm"
                              variant="light"
                            >
                              {entry.status}
                            </Badge>
                          ) : (
                            '-'
                          )}
                        </Table.Td>
                      </Table.Tr>
                    );
                  })}
                </Table.Tbody>
                <Table.Tfoot>
                  <Table.Tr>
                    <Table.Td colSpan={2}>
                      <Text fw={700}>Total</Text>
                    </Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>
                      <Text fw={700}>{hoursQuery.data?.total_hours?.toFixed(1) ?? '0.0'}</Text>
                    </Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>
                      <Text fw={700}>{hoursQuery.data?.total_ot?.toFixed(1) ?? '0.0'}</Text>
                    </Table.Td>
                    <Table.Td />
                  </Table.Tr>
                </Table.Tfoot>
              </Table>
            </>
          )}
        </Paper>

        {/* ===== Attendance Score ===== */}
        <Paper p="md" shadow="sm" radius="md" withBorder>
          <Group gap="xs" mb="md">
            <IconAlertCircle size={20} />
            <Text fw={600} size="lg">Attendance Score</Text>
          </Group>
          {scoreQuery.isLoading ? (
            <Center h={120}><Loader /></Center>
          ) : scoreQuery.isError ? (
            <Alert color="red" icon={<IconAlertCircle size={16} />}>
              Failed to load attendance score.
            </Alert>
          ) : (
            (() => {
              const total = scoreQuery.data?.total_points ?? 0;
              const max = scoreQuery.data?.max_points ?? 1;
              const color = getScoreColor(total, max);
              const pct = max > 0 ? (total / max) * 100 : 0;
              return (
                <Stack gap="md" align="center">
                  <Text size="xl" fw={700} c={color}>
                    {total} / {max} points
                  </Text>
                  <Progress
                    value={pct}
                    color={color}
                    size="xl"
                    radius="xl"
                    style={{ width: '100%' }}
                  />
                  <Text size="sm" c="dimmed">
                    {total === 0
                      ? 'Perfect attendance!'
                      : pct >= 75
                        ? 'Please review your attendance record.'
                        : 'Keep up the good work.'}
                  </Text>
                </Stack>
              );
            })()
          )}
        </Paper>
      </SimpleGrid>

      {/* ===== PTO Balances ===== */}
      <Paper p="md" shadow="sm" radius="md" withBorder>
        <Group gap="xs" mb="md">
          <IconBeach size={20} />
          <Text fw={600} size="lg">PTO Balances</Text>
        </Group>
        {ptoQuery.isLoading ? (
          <Center h={80}><Loader /></Center>
        ) : ptoQuery.isError ? (
          <Alert color="red" icon={<IconAlertCircle size={16} />}>
            Failed to load PTO balances.
          </Alert>
        ) : !ptoQuery.data?.balances?.length ? (
          <Text c="dimmed">No PTO balances found.</Text>
        ) : (
          <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="md">
            {ptoQuery.data.balances.map((bal) => {
              const pct = bal.total > 0 ? (bal.used / bal.total) * 100 : 0;
              return (
                <Paper key={bal.leave_type} p="md" radius="md" withBorder>
                  <Text fw={600} size="sm" tt="capitalize" mb="xs">
                    {bal.leave_type}
                  </Text>
                  <Text size="xl" fw={700}>
                    {bal.available}
                    <Text span size="sm" c="dimmed" fw={400}>
                      {' '}/ {bal.total} hrs
                    </Text>
                  </Text>
                  <Progress value={pct} color={pct > 80 ? 'red' : pct > 50 ? 'yellow' : 'blue'} size="sm" mt="xs" />
                  <Group gap="xs" mt="xs">
                    <Text size="xs" c="dimmed">Used: {bal.used}</Text>
                    {bal.pending > 0 && (
                      <Badge size="xs" color="yellow" variant="light">
                        {bal.pending} pending
                      </Badge>
                    )}
                  </Group>
                </Paper>
              );
            })}
          </SimpleGrid>
        )}
      </Paper>

      {/* ===== Upcoming Schedule ===== */}
      <Paper p="md" shadow="sm" radius="md" withBorder>
        <Group gap="xs" mb="md">
          <IconCalendar size={20} />
          <Text fw={600} size="lg">Upcoming Schedule (Next 7 Days)</Text>
        </Group>
        {scheduleQuery.isLoading ? (
          <Center h={120}><Loader /></Center>
        ) : scheduleQuery.isError ? (
          <Alert color="red" icon={<IconAlertCircle size={16} />}>
            Failed to load schedule.
          </Alert>
        ) : !scheduleQuery.data?.schedules?.length ? (
          <Text c="dimmed">No upcoming shifts scheduled.</Text>
        ) : (
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Date</Table.Th>
                <Table.Th>Day</Table.Th>
                <Table.Th>Shift Start</Table.Th>
                <Table.Th>Shift End</Table.Th>
                <Table.Th>Role</Table.Th>
                <Table.Th>Department</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {scheduleQuery.data.schedules.map((sched) => (
                <Table.Tr key={sched.id}>
                  <Table.Td>{dayjs(sched.date).format('MM/DD/YYYY')}</Table.Td>
                  <Table.Td>{dayjs(sched.date).format('dddd')}</Table.Td>
                  <Table.Td>{dayjs(sched.shift_start).format('h:mm A')}</Table.Td>
                  <Table.Td>{dayjs(sched.shift_end).format('h:mm A')}</Table.Td>
                  <Table.Td>{sched.role}</Table.Td>
                  <Table.Td>{sched.department}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Paper>
    </Stack>
  );
}
